import { API, ApiResponse, handleApiResponse, handleApiError } from './config';
import { User, LoginCredentials, OtpResponse, ValidateOtpResponse, TrustDeviceResponse, LogoutResponse } from '../../types';

/**
 * Servicio de autenticación para los endpoints del API de PayPlus
 */
export const authService = {
/**
 * Inicia sesión con número de teléfono y contraseña
 * @param mobile_phone Número de teléfono del usuario
 * @param password Contraseña del usuario
 * @param is_distributor Indica si es un distribuidor (opcional)
 * @returns Datos del usuario y token
 */
login: async (mobile_phone: string, password: string, is_distributor: boolean = false): Promise<ApiResponse<User & { token: string }>> => {
  try {
    // Intentar hacer la solicitud al backend real
    const response = await API.post('/login', { mobile_phone, password, is_distributor });
    
    // Obtener la respuesta y registrarla para depuración
    const responseData = response.data;
    console.log('Respuesta del backend:', JSON.stringify(responseData));
    
    // Si la respuesta tiene el formato esperado con success y data, devolverla directamente
    if (responseData && responseData.success !== undefined) {
      if (responseData.success && responseData.data) {
        // La respuesta es exitosa, devolver tal cual
        return responseData as ApiResponse<User & { token: string }>;
      } else {
        // La respuesta indica un error con el mensaje apropiado del backend
        return responseData as ApiResponse<User & { token: string }>;
      }
    }
    
    // Si llegamos aquí, la respuesta tiene un formato diferente al esperado
    // Pero igualmente la pasamos a un formato que la aplicación pueda entender
    return {
      success: false,
      message: 'Formato de respuesta desconocido del servidor',
      data: null
    };
  } catch (error: any) {
    console.error('Error completo de login:', error);
    
    // Si hay un error de respuesta del servidor (como 401, 500, etc)
    if (error.response) {
      // El servidor respondió con un código de error
      if (error.response.status === 500) {
        return {
          success: false,
          message: 'Error en el servidor: contraseña incorrecta o error interno',
          data: null
        };
      }
      
      // Si el backend devolvió un mensaje de error específico, usarlo
      if (error.response.data && error.response.data.message) {
        return {
          success: false,
          message: error.response.data.message,
          data: null
        };
      }
    }
    
    // Error de conexión u otro problema
    return {
      success: false,
      message: error.message || 'Error al conectar con el servidor',
      data: null
    };
  }
},
  
  /**
   * Envía un código OTP al teléfono registrado
   * @param deviceId ID único del dispositivo
   * @param deviceName Nombre del dispositivo
   * @returns Estado de la solicitud OTP
   */
  sendOtp: async (deviceId: string, deviceName: string): Promise<ApiResponse<OtpResponse['data']>> => {
    try {
      const response = await API.post('/send-otp', { deviceId, deviceName });
      
      // Obtener la respuesta y registrarla para depuración
      const responseData = response.data;
      console.log('Respuesta sendOTP del backend:', JSON.stringify(responseData));
      
      // Si la respuesta tiene el formato esperado, devolverla directamente
      if (responseData && responseData.success !== undefined) {
        return responseData as ApiResponse<OtpResponse['data']>;
      }
      
      // Si el formato es diferente, dar un mensaje claro
      return {
        success: false,
        message: 'Formato de respuesta desconocido del servidor para OTP',
        data: null
      };
    } catch (error: any) {
      console.error('Error en sendOtp:', error);
      
      // Si hay un error de respuesta del servidor
      if (error.response) {
        // Si el backend devolvió un mensaje de error específico, usarlo
        if (error.response.data && error.response.data.message) {
          return {
            success: false,
            message: error.response.data.message,
            data: null
          };
        }
      }
      
      // Error de conexión u otro problema
      return {
        success: false,
        message: error.message || 'Error al conectar con el servidor para enviar OTP',
        data: null
      };
    }
  },
  
  /**
   * Valida un código OTP recibido
   * @param otp Código OTP ingresado por el usuario
   * @param deviceId ID del dispositivo
   * @returns Confirmación de validación exitosa
   */
  validateOtp: async (otp: string, deviceId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await API.post('/validate-otp', { otp, deviceId });
      
      // Obtener la respuesta y registrarla para depuración
      const responseData = response.data;
      console.log('Respuesta validateOTP del backend:', JSON.stringify(responseData));
      
      // Si la respuesta tiene el formato esperado, devolverla directamente
      if (responseData && responseData.success !== undefined) {
        return responseData as ApiResponse<void>;
      }
      
      // Si el formato es diferente, dar un mensaje claro
      return {
        success: false,
        message: 'Formato de respuesta desconocido del servidor para validación OTP',
        data: null
      };
    } catch (error: any) {
      console.error('Error en validateOtp:', error);
      
      // Si hay un error de respuesta del servidor
      if (error.response) {
        // Si el backend devolvió un mensaje de error específico, usarlo
        if (error.response.data && error.response.data.message) {
          return {
            success: false,
            message: error.response.data.message,
            data: null
          };
        }
      }
      
      // Error de conexión u otro problema
      return {
        success: false,
        message: error.message || 'Error al conectar con el servidor para validar OTP',
        data: null
      };
    }
  },
  
  /**
   * Marca un dispositivo como confiable para omitir validación OTP futura
   * @param deviceId ID del dispositivo
   * @param trustDevice "1" para confiar, "0" para no confiar
   * @returns Confirmación de la acción
   */
  trustDevice: async (deviceId: string, trustDevice: string): Promise<ApiResponse<void>> => {
    try {
      const response = await API.post('/trust-device', { deviceId, trustDevice });
      
      // Obtener la respuesta y registrarla para depuración
      const responseData = response.data;
      console.log('Respuesta trustDevice del backend:', JSON.stringify(responseData));
      
      // Si la respuesta tiene el formato esperado, devolverla directamente
      if (responseData && responseData.success !== undefined) {
        return responseData as ApiResponse<void>;
      }
      
      // Si el formato es diferente, dar un mensaje claro
      return {
        success: false,
        message: 'Formato de respuesta desconocido del servidor para marcar dispositivo',
        data: null
      };
    } catch (error: any) {
      console.error('Error en trustDevice:', error);
      
      // Si hay un error de respuesta del servidor
      if (error.response) {
        // Si el backend devolvió un mensaje de error específico, usarlo
        if (error.response.data && error.response.data.message) {
          return {
            success: false,
            message: error.response.data.message,
            data: null
          };
        }
      }
      
      // Error de conexión u otro problema
      return {
        success: false,
        message: error.message || 'Error al conectar con el servidor para marcar dispositivo',
        data: null
      };
    }
  },
  
  /**
   * Cierra la sesión del usuario actual
   * @returns Confirmación de cierre de sesión
   */
  logout: async (): Promise<ApiResponse<void>> => {
    try {
      // Siempre eliminar el token local, independientemente de la respuesta del servidor
      localStorage.removeItem('token');
      
      const response = await API.post('/logout', {});
      
      // Obtener la respuesta y registrarla para depuración
      const responseData = response.data;
      console.log('Respuesta logout del backend:', JSON.stringify(responseData));
      
      // Si la respuesta tiene el formato esperado, devolverla directamente
      if (responseData && responseData.success !== undefined) {
        return responseData as ApiResponse<void>;
      }
      
      // Si el formato es diferente pero el logout local fue exitoso
      return {
        success: true,
        message: 'Sesión cerrada localmente (respuesta del servidor no estándar)',
        data: null
      };
    } catch (error: any) {
      console.error('Error en logout:', error);
      
      // El token ya se eliminó localmente, así que el logout es parcialmente exitoso
      return {
        success: true,
        message: 'Sesión cerrada localmente, pero hubo un error con el servidor: ' + (error.message || 'Error de conexión'),
        data: null
      };
    }
  },

  /**
   * Obtiene el perfil del usuario actualmente autenticado usando el token
   * Este método utiliza los datos almacenados localmente en lugar de hacer una petición a la API
   * @returns Datos del perfil del usuario
   */
  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'No hay sesión activa', data: null };
      }
      const response = await API.post('/get-profile', {});
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('Error en getCurrentUser:', error);
      return {
        success: false,
        message: error.message || 'Error al obtener perfil del usuario',
        data: null
      };
    }
  }
};
